import { useState } from "react";
import { MapPin, Search, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";

const COUNTIES = [
  { name: "Charles County", places: "Waldorf, La Plata, White Plains, Bryans Road, Indian Head, Hughesville" },
  { name: "Calvert County", places: "Prince Frederick, Lusby, Solomons, Huntingtown, Dunkirk, Chesapeake Beach" },
  { name: "Southern Prince George's", places: "Fort Washington, Oxon Hill, Clinton, Brandywine, Upper Marlboro (south of 20744)" },
];

export default function ServiceArea() {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (zip.length < 5) return;
    setLoading(true);
    try {
      const { data } = await api.post("/zip-check", { zip });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="service-area-page" className="pb-24">
      <div className="mx-auto max-w-3xl px-5 pt-20 text-center lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">Service Area</p>
        <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tighter md:text-5xl">
          Proudly serving Southern Maryland
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">
          Enter your ZIP to instantly check if we clean in your neighborhood.
        </p>
      </div>

      <div className="mx-auto mt-10 max-w-md px-5">
        <form onSubmit={check} className="flex gap-2">
          <input
            data-testid="zip-check-input"
            value={zip}
            onChange={(e) => { setZip(e.target.value.replace(/\D/g, "").slice(0, 5)); setResult(null); }}
            placeholder="Enter ZIP code"
            inputMode="numeric"
            className="flex-1 rounded-xl border border-border bg-muted/50 px-4 py-3 text-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            data-testid="zip-check-btn"
            type="submit"
            disabled={loading || zip.length < 5}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 font-bold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50"
          >
            <Search className="h-5 w-5" /> Check
          </button>
        </form>
        {result && (
          <div
            data-testid="zip-check-result"
            className={`mt-4 flex items-center gap-3 rounded-xl border p-4 ${
              result.in_area
                ? "border-primary bg-accent text-accent-foreground"
                : "border-destructive/40 bg-destructive/10 text-foreground"
            }`}
          >
            {result.in_area ? <CheckCircle2 className="h-6 w-6 text-primary" /> : <XCircle className="h-6 w-6 text-destructive" />}
            <p className="text-sm font-semibold">
              {result.in_area
                ? `Great news — ${result.zip} is in our service area!`
                : `We don't service ${result.zip} yet, but we're expanding. Leave your info on the booking page and we'll follow up.`}
            </p>
          </div>
        )}
      </div>

      <div className="mx-auto mt-16 grid max-w-5xl gap-5 px-5 md:grid-cols-2 lg:px-8">
        {COUNTIES.map((c) => (
          <div key={c.name} className="rounded-2xl border border-border bg-card p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <MapPin className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-heading text-xl font-bold">{c.name}</h3>
            <p className="mt-2 text-muted-foreground">{c.places}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
